import React, { useState, useEffect, useCallback } from 'react';
import {
  getVerificationById,
  getVerificationDocuments,
  updateVerificationStatus,
  updateDocumentStatus,
  uploadDocument,
  uploadFileToCloud
} from '../Config/verificationService';
import Topbar from '../Components/Topbar';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useParams, useNavigate } from 'react-router-dom';
import {
  formatEasternDate,
  formatEasternDateTime,
} from '../Utils/dateTime';

function VerificationDetails() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [verification, setVerification] = useState(null);
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showStatusModal, setShowStatusModal] = useState(false);
  const [showDocumentModal, setShowDocumentModal] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [statusForm, setStatusForm] = useState({
    status: '',
    admin_remark: ''
  });
  const [documentStatusForm, setDocumentStatusForm] = useState({
    status: '',
    admin_remark: ''
  });
  const [uploadForm, setUploadForm] = useState({
    document_type: '',
    document_value: '',
    is_file: true,
    admin_remark: '',
    expiry_date: ''
  });
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);

  const documentTypes = [
    'BUSINESS_LICENSE',
    'TRADE_CERTIFICATION',
    'LIABILITY_INSURANCE',
    'WSIB',
    'PHOTO_ID',
    'PORTFOLIO',
    'BUSINESS_LOGO',
    'WORKER_CERTIFICATION',
    'WORKER_LICENSE',
    'WORKER_INSURANCE',
    'WORKER_ID',
    'WORKER_PORTFOLIO',
    'GST_HST_NUMBER',
    'BANKING_INFO',
    'WSIB_NUMBER',
    'BUSINESS_LICENSE_NUMBER',
    'OTHER'
  ];

  const fetchVerificationDetails = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getVerificationById(id);
      if (response.success) {
        setVerification(response.verification);
      } else {
        setError('Failed to fetch verification details');
        toast.error('Failed to fetch verification details');
      }
    } catch (error) {
      setError('Failed to fetch verification details');
      toast.error('Failed to fetch verification details');
    }
    setLoading(false);
  }, [id]);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await getVerificationDocuments(id);
      if (response.success) {
        setDocuments(response.documents);
      } else {
        toast.error('Failed to fetch documents');
      }
    } catch (error) {
      toast.error('Failed to fetch documents');
    }
  }, [id]);

  useEffect(() => {
    fetchVerificationDetails();
    fetchDocuments();
  }, [fetchVerificationDetails, fetchDocuments]);

  const handleStatusUpdate = () => {
    setStatusForm({
      status: verification.status,
      admin_remark: ''
    });
    setShowStatusModal(true);
  };

  const handleStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await updateVerificationStatus(id, statusForm);
      if (response.success) {
        toast.success('Verification status updated successfully');
        setShowStatusModal(false);
        fetchVerificationDetails();
      } else {
        toast.error('Failed to update verification status');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleDocumentStatusUpdate = (document) => {
    setSelectedDocument(document);
    setDocumentStatusForm({
      status: document.status,
      admin_remark: ''
    });
    setShowDocumentModal(true);
  };

  const handleDocumentStatusSubmit = async (e) => {
    e.preventDefault();
    try {
      const response = await updateDocumentStatus(selectedDocument.id, documentStatusForm);
      if (response.success) {
        toast.success('Document status updated successfully');
        setShowDocumentModal(false);
        fetchDocuments();
      } else {
        toast.error('Failed to update document status');
      }
    } catch (error) {
      toast.error('An error occurred');
    }
  };

  const handleUploadDocument = () => {
    setUploadForm({
      document_type: '',
      document_value: '',
      is_file: true,
      admin_remark: '',
      expiry_date: ''
    });
    setSelectedFile(null);
    setShowUploadModal(true);
  };

  const handleUploadSubmit = async (e) => {
    e.preventDefault();

    if (!selectedFile && uploadForm.is_file) {
      toast.error('Please select a file to upload');
      return;
    }

    if (!uploadForm.document_value && !uploadForm.is_file) {
      toast.error('Please enter a document value');
      return;
    }

    setUploading(true);
    try {
      let documentValue = uploadForm.document_value;

      // If it's a file upload, upload to cloud first
      if (uploadForm.is_file && selectedFile) {
        documentValue = await uploadFileToCloud(selectedFile);
        toast.success('File uploaded to cloud successfully');
      }

      const uploadData = {
        ...uploadForm,
        document_value: documentValue,
        expiry_date: uploadForm.expiry_date ? new Date(uploadForm.expiry_date).toISOString() : null
      };

      const response = await uploadDocument(id, uploadData);
      if (response.success) {
        toast.success('Document uploaded successfully');
        setShowUploadModal(false);
        setSelectedFile(null);
        fetchDocuments();
      } else {
        toast.error('Failed to upload document');
      }
    } catch (error) {
      toast.error('An error occurred: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'VERIFIED':
      case 'APPROVED':
        return 'bg-green-100 text-green-800';
      case 'REJECTED':
        return 'bg-red-100 text-red-800';
      case 'SUSPENDED':
      case 'EXPIRED':
      case 'REVOKED':
        return 'bg-orange-100 text-orange-800';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const getVerificationTypeColor = (type) => {
    switch (type) {
      case 'CONTRACTOR':
        return 'bg-blue-100 text-blue-800';
      case 'WORKER':
        return 'bg-purple-100 text-purple-800';
      case 'ADMIN':
        return 'bg-indigo-100 text-indigo-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDocumentType = (type) => {
    return type.replace(/_/g, ' ').toLowerCase().replace(/\b\w/g, l => l.toUpperCase());
  };

  const renderDocumentValue = (document) => {
    if (document.is_file) {
      return (
        <a
          href={document.document_value}
          target="_blank"
          rel="noopener noreferrer"
          className="text-blue-600 hover:text-blue-800 underline"
        >
          View Document
        </a>
      );
    } else {
      return (
        <span className="text-gray-900">
          {document.document_value.length > 50
            ? `${document.document_value.substring(0, 50)}...`
            : document.document_value}
        </span>
      );
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Topbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center">Loading...</div>
        </div>
      </div>
    );
  }

  if (error || !verification) {
    return (
      <div className="min-h-screen bg-gray-100">
        <Topbar />
        <div className="container mx-auto px-4 py-8">
          <div className="text-center text-red-600">{error || 'Verification not found'}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <Topbar />
      <div className="container mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center space-x-4">
            <button
              onClick={() => navigate('/verifications')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold text-white shadow-md transition-all duration-200 hover:opacity-90 hover:shadow-lg active:scale-95"
              style={{ background: 'linear-gradient(135deg, #002F53 0%, #004a82 100%)' }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Back to Verifications
            </button>
            <h1 className="text-2xl font-bold">Verification Details</h1>
          </div>
          <div className="space-x-2">
            <button
              onClick={handleStatusUpdate}
              className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#002F53' }}
            >
              Update Status
            </button>
            <button
              onClick={handleUploadDocument}
              className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"
              style={{ backgroundColor: '#EF5E28' }}
            >
              Upload Document
            </button>
          </div>
        </div>

        {/* Verification Overview */}
        <div className="bg-white rounded-lg shadow p-6 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <h2 className="text-lg font-semibold mb-4">User Information</h2>
              <div className="space-y-2 text-left">
                <div><strong>Name:</strong> {verification.user.name}</div>
                <div><strong>Email:</strong> {verification.user.email}</div>
                <div><strong>Mobile:</strong> {verification.user.mobile}</div>
                {verification.user.business_name && (
                  <div><strong>Business Name:</strong> {verification.user.business_name}</div>
                )}
                <div><strong>Gender:</strong> {verification.user.gender}</div>
                <div><strong>Age:</strong> {verification.user.age}</div>
              </div>
            </div>
            <div>
              <h2 className="text-lg font-semibold mb-4">Verification Details</h2>
              <div className="space-y-2 text-left">
                <div>
                  <strong>Type:</strong>{' '}
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getVerificationTypeColor(verification.verification_type)}`}>
                    {verification.verification_type}
                  </span>
                </div>
                <div>
                  <strong>Status:</strong>{' '}
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(verification.status)}`}>
                    {verification.status}
                  </span>
                </div>
                <div><strong>Created:</strong> {formatEasternDateTime(verification.created_at)}</div>
                {verification.verified_at && (
                  <div><strong>Verified:</strong> {formatEasternDateTime(verification.verified_at)}</div>
                )}
                {verification.admin_remark && (
                  <div><strong>Admin Remark:</strong> {verification.admin_remark}</div>
                )}
              </div>
            </div>
          </div>

          {/* Additional Verification Details */}
          {(verification.years_of_experience || verification.emergency_service || verification.languages_spoken?.length > 0) && (
            <div className="mt-6 pt-6 border-t">
              <h2 className="text-lg font-semibold mb-4">Additional Information</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {verification.years_of_experience && (
                  <div><strong>Years of Experience:</strong> {verification.years_of_experience}</div>
                )}
                <div><strong>Emergency Service:</strong> {verification.emergency_service ? 'Yes' : 'No'}</div>
                <div><strong>Vehicle Available:</strong> {verification.vehicle_available ? 'Yes' : 'No'}</div>
                {verification.languages_spoken?.length > 0 && (
                  <div><strong>Languages:</strong> {verification.languages_spoken.join(', ')}</div>
                )}
                {verification.payment_frequency && (
                  <div><strong>Payment Frequency:</strong> {verification.payment_frequency}</div>
                )}
                <div><strong>Terms Accepted:</strong> {verification.terms_accepted ? 'Yes' : 'No'}</div>
              </div>
            </div>
          )}
        </div>

        {/* Documents Section */}
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold">Documents ({documents.length})</h2>
          </div>
          {documents.length === 0 ? (
            <div className="p-8 text-center text-gray-500">No documents uploaded</div>
          ) : (
            <div className="overflow-x-auto text-left">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Document Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Value
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Expiry Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Uploaded
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {documents.map((document) => (
                    <tr key={document.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {formatDocumentType(document.document_type)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {renderDocumentValue(document)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(document.status)}`}>
                          {document.status}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {document.expiry_date ? formatEasternDate(document.expiry_date) : 'N/A'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {formatEasternDate(document.created_at)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <button
                          onClick={() => handleDocumentStatusUpdate(document)}
                          className="font-semibold hover:opacity-80 transition-opacity"
                          style={{ color: '#002F53' }}
                        >
                          Update Status
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Status Update Modal */}
        {showStatusModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[120]">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Update Verification Status
                </h3>
                <form onSubmit={handleStatusSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={statusForm.status}
                      onChange={(e) => setStatusForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="PENDING">Pending</option>
                      <option value="VERIFIED">Verified</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="SUSPENDED">Suspended</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Remark
                    </label>
                    <textarea
                      value={statusForm.admin_remark}
                      onChange={(e) => setStatusForm(prev => ({ ...prev, admin_remark: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      rows="3"
                      placeholder="Enter your remarks..."
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowStatusModal(false)}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: '#002F53' }}
                    >
                      Update Status
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Document Status Update Modal */}
        {showDocumentModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[120]">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Update Document Status
                </h3>
                <form onSubmit={handleDocumentStatusSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Status
                    </label>
                    <select
                      value={documentStatusForm.status}
                      onChange={(e) => setDocumentStatusForm(prev => ({ ...prev, status: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="PENDING">Pending</option>
                      <option value="APPROVED">Approved</option>
                      <option value="REJECTED">Rejected</option>
                      <option value="EXPIRED">Expired</option>
                      <option value="REVOKED">Revoked</option>
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Remark
                    </label>
                    <textarea
                      value={documentStatusForm.admin_remark}
                      onChange={(e) => setDocumentStatusForm(prev => ({ ...prev, admin_remark: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      rows="3"
                      placeholder="Enter your remarks..."
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowDocumentModal(false)}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity"
                      style={{ backgroundColor: '#002F53' }}
                    >
                      Update Status
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Upload Document Modal */}
        {showUploadModal && (
          <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[120]">
            <div className="relative top-20 mx-auto p-5 border w-96 shadow-lg rounded-md bg-white">
              <div className="mt-3">
                <h3 className="text-lg font-medium text-gray-900 mb-4">
                  Upload Document
                </h3>
                <form onSubmit={handleUploadSubmit}>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Document Type
                    </label>
                    <select
                      value={uploadForm.document_type}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, document_type: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      required
                    >
                      <option value="">Select Document Type</option>
                      {documentTypes.map(type => (
                        <option key={type} value={type}>
                          {formatDocumentType(type)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="mb-4">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        checked={uploadForm.is_file}
                        onChange={(e) => {
                          setUploadForm(prev => ({ ...prev, is_file: e.target.checked }));
                          setSelectedFile(null);
                        }}
                        className="mr-2"
                      />
                      <span className="text-sm font-medium text-gray-700">Upload File</span>
                    </label>
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      {uploadForm.is_file ? 'Select File' : 'Document Value'}
                    </label>
                    {uploadForm.is_file ? (
                      <div>
                        <input
                          type="file"
                          onChange={(e) => {
                            const file = e.target.files[0];
                            if (file) {
                              // Check file size (10MB limit)
                              if (file.size > 10 * 1024 * 1024) {
                                toast.error('File size must be less than 10MB');
                                e.target.value = '';
                                return;
                              }
                              setSelectedFile(file);
                            }
                          }}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                          accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.gif"
                          required
                        />
                        <p className="text-xs text-gray-500 mt-1">
                          Supported formats: PDF, DOC, DOCX, JPG, JPEG, PNG, GIF (Max 10MB)
                        </p>
                        {selectedFile && (
                          <p className="text-sm text-gray-600 mt-1">
                            Selected: {selectedFile.name} ({(selectedFile.size / 1024 / 1024).toFixed(2)} MB)
                          </p>
                        )}
                      </div>
                    ) : (
                      <input
                        type="text"
                        value={uploadForm.document_value}
                        onChange={(e) => setUploadForm(prev => ({ ...prev, document_value: e.target.value }))}
                        className="w-full border border-gray-300 rounded-md px-3 py-2"
                        placeholder="Enter URL or text value"
                        required
                      />
                    )}
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Expiry Date (Optional)
                    </label>
                    <input
                      type="date"
                      value={uploadForm.expiry_date}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, expiry_date: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                    />
                  </div>
                  <div className="mb-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Admin Remark
                    </label>
                    <textarea
                      value={uploadForm.admin_remark}
                      onChange={(e) => setUploadForm(prev => ({ ...prev, admin_remark: e.target.value }))}
                      className="w-full border border-gray-300 rounded-md px-3 py-2"
                      rows="3"
                      placeholder="Enter your remarks..."
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <button
                      type="button"
                      onClick={() => setShowUploadModal(false)}
                      disabled={uploading}
                      className="bg-gray-500 text-white px-4 py-2 rounded hover:bg-gray-600 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      disabled={uploading}
                      className="text-white px-4 py-2 rounded font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
                      style={{ backgroundColor: '#EF5E28' }}
                    >
                      {uploading ? 'Uploading...' : 'Upload Document'}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}
      </div>
      <ToastContainer />
    </div>
  );
}

export default VerificationDetails; 
